import {APPROVAL_STATUS} from '@/constants';
import {getCompanyLockedDate, getLockedDate} from './TimeLockUtil';

// A date as a sortable integer. Relay nodes carry year/month/day as separate 1-based numbers,
// so comparing them this way keeps this module free of the heavyweight `Util` import (and the
// import cycle that would come with it) that a Moment conversion needs.
const dateKey = (year: number, month: number, day: number): number => year * 10000 + month * 100 + day;

type WeekApprovalRow = {
	weekStartYear: number;
	weekStartMonth: number;
	weekStartDay: number;
	approvalStatus: string;
};

/**
 * Has the approver sent this person-week back to them?
 *
 * The rule, and the only place it is written down. Two-sourced, and it has to be: per-week
 * approval rows are only ever written for weeks submitted with **no hours on them**, so a week
 * with entries that gets declined has no such row at all, and sourcing this from those rows alone
 * would report "not declined" for exactly the weeks that need correcting. So:
 *
 *   1. any declined row in the week settles it;
 *   2. otherwise the per-week row answers, but only while every in-scope row in the week is still
 *      pre-submission — a zero-hour week's first correcting entry is born PENDING and must not
 *      re-lock its owner, while a week whose rows have been re-submitted has moved on.
 *
 * Same precedence the employee's own timesheet applies when it labels the week (see
 * `getApprovalStatusForWeek`), so the pill on screen and the lock cannot disagree.
 *
 * Rows in scope are the ones timesheet approval governs, the same cut the server makes in
 * `TimeApprovalLogic.isSubmitLockRelaxedForDate`: project time always, internal time where the
 * company approves it (`usingInternalTimeApproval`), never time off — that carries its own
 * approval flow, owned by the allocation schedule rather than the timesheet approver. Excluding
 * *all* idle rows, as an earlier cut did, left a week declined only on internal time unlockable
 * here while the server allowed the create and the delete.
 *
 * Note the server is the real gate and reads raw statuses. The read side projects DECLINED to
 * NOT_SUBMITTED while the new-statuses flag is off, so with the flag off this returns false and
 * nothing changes here — but the backend still lets the owner correct a week that was declined
 * while the flag was on.
 *
 * @param weekStart first day of the week, as year/month/day (month 1-based)
 * @param timeRegNodes the person's rows; may span a wider period, and are filtered to the week here
 */
export const isWeekDeclined = (
	weekStart: {year: number; month: number; day: number},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeRegNodes: any[] | null | undefined,
	weekApprovals: WeekApprovalRow[] | null | undefined,
	// Required, not defaulted: leaving internal time out of the scope was the bug, and a default
	// would let the next call site reacquire it silently.
	usingInternalTimeApproval: boolean
): boolean => {
	// The seven day-keys of the week, enumerated rather than treated as a numeric range: a week
	// crossing a month boundary is not contiguous in `dateKey` space. UTC throughout, so the
	// stepping cannot be shifted by a daylight-saving transition mid-week.
	const weekKeys = new Set<number>();
	const start = new Date(Date.UTC(weekStart.year, weekStart.month - 1, weekStart.day));
	for (let offset = 0; offset < 7; offset++) {
		const day = new Date(start.getTime());
		day.setUTCDate(day.getUTCDate() + offset);
		weekKeys.add(dateKey(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate()));
	}
	const inWeek = (node: {year: number; month: number; day: number}): boolean =>
		weekKeys.has(dateKey(node.year, node.month, node.day));

	const inScope = (node: {idleTime?: {isInternalTime?: boolean | null} | null}): boolean =>
		!node.idleTime || (usingInternalTimeApproval && !!node.idleTime.isInternalTime);

	const rowsInScope = (timeRegNodes ?? [])
		.map(entry => entry?.node ?? entry)
		.filter(node => node && inScope(node) && inWeek(node));

	if (rowsInScope.some(node => node.approvalStatus === APPROVAL_STATUS.DECLINED)) {
		return true;
	}

	// The per-week row still answers while every row in scope for the week is pre-submission. A
	// zero-hour week carries its decline on that row, and the owner's first correcting entry is
	// born PENDING — handing the answer to the rows the moment one exists would report "not
	// declined" and re-lock them after a single entry, which is exactly the case this arm exists
	// for. Once a row is submitted or beyond, the week has moved on and a leftover declined week
	// row must stop unlocking it.
	const atOrPastSubmission: string[] = [APPROVAL_STATUS.SUBMITTED, APPROVAL_STATUS.APPROVED, APPROVAL_STATUS.INVOICED];
	if (rowsInScope.some(node => atOrPastSubmission.includes(node.approvalStatus))) {
		return false;
	}

	const row = (weekApprovals ?? []).find(
		r => r.weekStartYear === weekStart.year && r.weekStartMonth === weekStart.month && r.weekStartDay === weekStart.day
	);
	return row?.approvalStatus === APPROVAL_STATUS.DECLINED;
};

/**
 * `isWeekDeclined` for callers holding a Moment and a person object — the My Timesheets grid,
 * where the person fragment carries `weekApprovals` and the rows may belong to several people.
 */
export const isPersonWeekDeclined = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	person: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dateInWeek: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeRegs: any[] | null | undefined,
	usingInternalTimeApproval: boolean
): boolean => {
	if (!person || !dateInWeek) return false;

	const weekStart = dateInWeek.clone().startOf('week');
	// `node.person` is absent on some callers' fragments; when it is present, only the selected
	// person's rows count (a manager can be viewing someone else's timesheet).
	const ownRows = (timeRegs ?? [])
		.map(entry => entry?.node ?? entry)
		.filter(node => node && !(node.person && person.id && node.person.id !== person.id));

	return isWeekDeclined(
		{year: weekStart.year(), month: weekStart.month() + 1, day: weekStart.date()},
		ownRows,
		person.weekApprovals ?? [],
		usingInternalTimeApproval
	);
};

/**
 * The lock date that should apply on My Timesheets for the week being viewed.
 *
 * Identical to `getLockedDate` except that a declined week drops the person's own submission
 * date, which is stale by definition once the approver has sent the week back: declining
 * deliberately does not roll `submit_locked_date` back, because that date is a single per-person
 * value and rolling it back would also unlock every later week, including approved ones. A
 * genuine company-wide lock still applies, and still honours the person's exclude-from-lock
 * exception, because that is what `getCompanyLockedDate` returns.
 *
 * Fixing the lock date at source is what makes the padlocks, the day-cell click and the row-level
 * delete/edit affordances agree — they all read a `lockedDate` handed down from here rather than
 * each deciding for themselves.
 */
export const getEffectiveLockedDate = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	company: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	person: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dateInWeek: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeRegs: any[] | null | undefined,
	usingInternalTimeApproval: boolean
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any =>
	isPersonWeekDeclined(person, dateInWeek, timeRegs, usingInternalTimeApproval)
		? getCompanyLockedDate(company, person)
		: getLockedDate(company, person);
